<template>
  <div
    class="draw-card horizontal"
    tabindex="0"
    role="button"
    @click="handleCardClick"
  >
    <div class="horizontal-card-content">
      <div
        class="left-group"
        @click="navigateToDetail"
      >
        <div class="flag-container">
          <CountryAvatar
            v-if="isLite"
            :country="lottery.country"
            :fallback-label="lottery.game_name"
            size="md"
          />
          <div
            v-else
            class="flag-border"
          >
            <img
              :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)"
              :alt="lottery.country + ' flag'"
              class="flag"
            >
          </div>
        </div>
        <h3
          class="lottery-name"
          :title="lottery.game_name"
        >
          {{ lottery.game_name }}
        </h3>
      </div>
      
      <div class="right-group">
        <button
          v-if="!isLite"
          class="favorite-btn-inline"
          :class="{ active: isFavorited }"
          :aria-label="$t('common.toggleFavorite')"
          @click.stop="$emit('favorite')"
        >
          <div class="favorite-icon-wrapper">
            <svg 
              viewBox="0 0 24 24" 
              class="favorite-icon-svg"
              :class="{ filled: isFavorited }"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        </button>
        
        <div
          class="countdown-section"
          @click="navigateToDetail"
        >
          <div class="time-block">
            <div class="time">
              {{ String(countdown.hours).padStart(2, '0') }}
            </div>
            <div class="label">
              {{ $t('time.hours') }}
            </div>
          </div>
          <div class="time-block">
            <div class="time">
              {{ String(countdown.minutes).padStart(2, '0') }}
            </div>
            <div class="label">
              {{ $t('time.mins') }}
            </div>
          </div>
          <div class="time-block">
            <div class="time">
              {{ String(countdown.seconds).padStart(2, '0') }}
            </div>
            <div class="label">
              {{ $t('time.secs') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getGameLogoUrl } from '@/utils/resultParser';
import type { UpcomingDraw } from '@/types/lottery';
import { defineProps, defineEmits } from 'vue'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import CountryAvatar from '@/components/base/CountryAvatar.vue'

defineProps<{
  lottery: UpcomingDraw;
  isFavorited: boolean;
  countdown: { hours: number; minutes: number; seconds: number };
  favoriteIcon: string;
  favoriteFilledIcon: string;
}>();

const emit = defineEmits(['favorite', 'navigate']);
const { isLite } = useIntegrationMode()

const navigateToDetail = () => {
  emit('navigate');
};

const handleCardClick = (event: MouseEvent) => {
  if (event.target === event.currentTarget) {
    navigateToDetail();
  }
};
</script>

<style scoped>
.draw-card.horizontal {
  height: var(--horizontal-lottery-card-height, 80px);
  width: 100%;
  max-width: 100%;
  padding: 0;
  background: var(--horizontal-lottery-card-bg, var(--surface-card-face, var(--colour-surface-cont-low)));
  display: flex;
  align-items: center;
  border: var(--horizontal-lottery-card-border-width, 1px) solid var(--horizontal-lottery-card-border-color, var(--colour-surface-cont));
  border-radius: var(--horizontal-lottery-card-radius, var(--radius-md));
  cursor: pointer;
  overflow: hidden;
  transition: background 0.2s ease;
  box-sizing: border-box;
}

.draw-card.horizontal:hover {
  background: color-mix(in srgb, var(--colour-surface-cont-low) 92%, var(--colour-orange-button) 8%);
}

@media (max-width: 768px) {
  .draw-card.horizontal:hover {
    background: var(--horizontal-lottery-card-bg, var(--surface-card-face, var(--colour-surface-cont-low)));
  }

  .favorite-btn-inline:hover {
    transform: none;
  }
}

.horizontal-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 0;
  background: var(--horizontal-lottery-card-bg, var(--surface-card-face, var(--colour-surface-cont-low))) !important;
}

.left-group {
  display: flex;
  align-items: center;
  gap: var(--horizontal-lottery-card-left-gap, 20px);
  flex: 1;
  min-width: 0;
  padding-left: var(--horizontal-lottery-card-left-padding, 20px);
}

.right-group {
  display: flex;
  align-items: center;
  gap: var(--horizontal-lottery-card-right-gap, 20px);
  flex-shrink: 0;
  height: 100%;
}

.flag-container {
  width: var(--horizontal-lottery-card-flag-size, 50px);
  height: var(--horizontal-lottery-card-flag-size, 50px);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.flag-border {
  width: 100%;
  height: 90%;
  border: var(--horizontal-lottery-card-flag-border-width, 2px) solid var(--colour-icons);
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--horizontal-lottery-card-flag-bg, var(--surface-card-disc, var(--colour-surface-cont-low)));
}

.flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lottery-name {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--horizontal-lottery-card-title-size, 18px);
  font-weight: var(--horizontal-lottery-card-title-weight, 400);
  letter-spacing: var(--horizontal-lottery-card-title-spacing, 0.01em);
  color: var(--lottery-name-color, var(--text-card-title));
  margin: 0;
  white-space: var(--horizontal-lottery-card-title-white-space, normal);
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: var(--horizontal-lottery-card-title-lines, 2);
  -webkit-box-orient: vertical;
}

.favorite-btn-inline {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--horizontal-lottery-card-favorite-padding, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.favorite-btn-inline:hover {
  transform: scale(1.1);
}

.favorite-icon-wrapper {
  width: var(--horizontal-lottery-card-favorite-size, 24px);
  height: var(--horizontal-lottery-card-favorite-size, 24px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--horizontal-lottery-card-favorite-radius, var(--favorite-icon-bg-radius, 50%));
}

.favorite-icon-svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: var(--horizontal-lottery-card-favorite-color, var(--favorite-icon-color, var(--text-card-title)));
  stroke-width: var(--horizontal-lottery-card-favorite-stroke, 1.5px);
  transition: all 0.2s ease;
}

.favorite-icon-svg.filled {
  fill: var(--horizontal-lottery-card-favorite-active-color, var(--favorite-icon-active-color, var(--colour-orange-button)));
  stroke: var(--horizontal-lottery-card-favorite-active-color, var(--favorite-icon-active-color, var(--colour-orange-button)));
}

.countdown-section {
  display: flex;
  gap: var(--horizontal-lottery-card-countdown-gap, 16px);
  background: var(--horizontal-lottery-card-countdown-bg, var(--surface-card-band, var(--colour-surface-cont-low)));
  padding: var(--horizontal-lottery-card-countdown-padding, 0 24px);
  min-width: var(--horizontal-lottery-card-countdown-width, 180px);
  height: 100%;
  align-items: center;
  color: var(--horizontal-lottery-card-countdown-color, var(--color, var(--colour-text-inverse)));
  justify-content: center;
}

.time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* min-width: 36px; */
}

.time {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--horizontal-lottery-card-time-size, 25px);
  font-style: normal;
  font-weight: var(--horizontal-lottery-card-time-weight, 700);
  line-height: normal;
  text-align: center;
  color: var(--horizontal-lottery-card-time-color, var(--color, var(--colour-text-inverse)));
}

.label {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--horizontal-lottery-card-label-size, 11px);
  font-weight: 400;
  line-height: normal;
  text-align: center;
  margin-top: var(--horizontal-lottery-card-label-margin-top, 4px);
  opacity: var(--horizontal-lottery-card-label-opacity, 0.9);
  color: var(--horizontal-lottery-card-label-color, var(--color, var(--colour-text-inverse)));
}

/* Tablet/iPad optimizations */
@media (max-width: 1200px) {
  .draw-card.horizontal {
    height: var(--horizontal-lottery-card-tablet-height, 75px);
  }
  
  .left-group {
    padding-left: var(--horizontal-lottery-card-tablet-left-padding, 16px);
    gap: var(--horizontal-lottery-card-tablet-left-gap, 16px);
  }
  
  .right-group {
    gap: var(--horizontal-lottery-card-tablet-right-gap, 16px);
  }
  
  .flag-container {
    width: var(--horizontal-lottery-card-tablet-flag-size, 45px);
    height: var(--horizontal-lottery-card-tablet-flag-size, 45px);
  }
  
  .lottery-name {
    font-size: var(--horizontal-lottery-card-tablet-title-size, 17px);
  }
  
  .countdown-section {
    min-width: var(--horizontal-lottery-card-tablet-countdown-width, 160px);
    padding: var(--horizontal-lottery-card-tablet-countdown-padding, 0 20px);
    gap: var(--horizontal-lottery-card-tablet-countdown-gap, 12px);
  }
  
  .time {
    font-size: var(--horizontal-lottery-card-tablet-time-size, 22px);
  }
  
  .label {
    font-size: var(--horizontal-lottery-card-tablet-label-size, 10px);
  }
}

@media (max-width: 768px) {
  .draw-card.horizontal {
    height: var(--horizontal-lottery-card-mobile-height, 70px);
    width: 100%;
    position: relative;
  }
  
  .left-group {
    padding-left: var(--horizontal-lottery-card-mobile-left-padding, 16px);
    gap: var(--horizontal-lottery-card-mobile-left-gap, 14px);
    max-width: var(--horizontal-lottery-card-mobile-left-max-width, calc(100% - 150px));
  }
  
  .flag-container {
    width: var(--horizontal-lottery-card-mobile-flag-size, 42px);
    height: var(--horizontal-lottery-card-mobile-flag-size, 42px);
  }
  
  .lottery-name {
    font-size: var(--horizontal-lottery-card-mobile-title-size, 13px);
    -webkit-line-clamp: var(--horizontal-lottery-card-mobile-title-lines, 2);
  }
  
  .right-group {
    gap: var(--horizontal-lottery-card-mobile-right-gap, 12px);
  }
  
  .favorite-btn-inline {
    padding: var(--horizontal-lottery-card-mobile-favorite-padding, 6px);
  }
  
  .favorite-icon-wrapper {
    width: var(--horizontal-lottery-card-mobile-favorite-size, 22px);
    height: var(--horizontal-lottery-card-mobile-favorite-size, 22px);
  }

  .countdown-section {
    min-width: var(--horizontal-lottery-card-mobile-countdown-width, 130px);
    padding: var(--horizontal-lottery-card-mobile-countdown-padding, 0 16px);
    gap: var(--horizontal-lottery-card-mobile-countdown-gap, 10px);
  }
  
  .time {
    font-size: var(--horizontal-lottery-card-mobile-time-size, 20px);
  }
  
  .label {
    font-size: var(--horizontal-lottery-card-mobile-label-size, 9px);
  }
}

@media (max-width: 480px) {
  .draw-card.horizontal {
    height: var(--horizontal-lottery-card-small-height, 65px);
  }
  
  .left-group {
    padding-left: var(--horizontal-lottery-card-small-left-padding, 12px);
    gap: var(--horizontal-lottery-card-small-left-gap, 12px);
    max-width: var(--horizontal-lottery-card-small-left-max-width, calc(100% - 130px));
  }
  
  .flag-container {
    width: var(--horizontal-lottery-card-small-flag-size, 38px);
    height: var(--horizontal-lottery-card-small-flag-size, 38px);
  }
  
  .lottery-name {
    font-size: var(--horizontal-lottery-card-small-title-size, 12px);
  }
  
  .favorite-btn-inline {
    padding: var(--horizontal-lottery-card-small-favorite-padding, 4px);
  }
  
  .favorite-icon-wrapper {
    width: var(--horizontal-lottery-card-small-favorite-size, 20px);
    height: var(--horizontal-lottery-card-small-favorite-size, 20px);
  }

  .countdown-section {
    min-width: var(--horizontal-lottery-card-small-countdown-width, 100px);
    padding: var(--horizontal-lottery-card-small-countdown-padding, 0 12px);
    gap: var(--horizontal-lottery-card-small-countdown-gap, 8px);
  }
  
  .time {
    font-size: var(--horizontal-lottery-card-small-time-size, 18px);
  }
  
  .label {
    font-size: var(--horizontal-lottery-card-small-label-size, 8px);
  }
}
</style>
